/** «Ayuda y manual»: todos los temas que el usuario puede ver, con buscador. Cada uno se despliega con sus pestañas y ventanas. */
import { BookOpen, ChevronDown, ChevronRight, Search } from 'lucide-react';
import { useState } from 'react';
import { Input } from '@/componentes/ui/input.tsx';
import { TituloPagina } from '@/layout/AppLayout.tsx';
import { useAuth } from '@/lib/auth.tsx';
import { ContenidoTema, TextoAyuda } from './PanelAyuda.tsx';
import { hijosDe, normalizar, TEMAS, temaPorId, textoDe, visiblePara } from './registro.ts';
import type { TemaAyuda } from './tipos.ts';

export function PaginaAyuda() {
  const { puede } = useAuth();
  const [q, setQ] = useState('');
  const [abiertos, setAbiertos] = useState<Set<string>>(new Set());
  const ver = (t: TemaAyuda) => visiblePara(t.permiso, (c, n) => puede(c, n));
  const busca = normalizar(q.trim());
  // Con búsqueda: un tema principal aparece si él o alguna de sus pestañas o ventanas coincide
  // Solo el texto que este usuario ve: un cajero que busca «tasa 2» no encuentra los bloques de la dueña (pedido de la dueña 2026-10-19)
  const puedeVer = (c: Parameters<typeof puede>[0], n?: Parameters<typeof puede>[1]) => puede(c, n);
  const coincide = (t: TemaAyuda) => !busca || normalizar(textoDe(t, puedeVer)).includes(busca) || hijosDe(t.id).some((h) => ver(h) && normalizar(textoDe(h, puedeVer)).includes(busca));
  const principales = TEMAS.filter((t) => !t.padre && ver(t) && coincide(t));
  const alternar = (id: string) => setAbiertos((a) => { const n = new Set(a); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  // Un subtema se ve dentro de su pantalla: se despliega la pantalla y se baja hasta él
  const ir = (id: string) => {
    const principal = temaPorId(id)?.padre ?? id;
    setAbiertos((a) => new Set(a).add(principal));
    setTimeout(() => document.getElementById(`ayuda-${id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
  };
  return (
    <div className="mx-auto max-w-4xl" data-testid="pagina-ayuda">
      <TituloPagina titulo="Ayuda y manual" subtitulo="Cómo se usa cada pantalla. En cualquier pantalla, el botón Ayuda (o F1) abre la de esa pantalla." />
      <div className="relative mb-4">
        <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
        <Input placeholder="Buscar: vuelto, IGTF, tasa, devolución, conteo…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-8" autoFocus data-testid="ayuda-buscar" />
      </div>
      {principales.length === 0 ? <p className="rounded-xl border border-dashed py-8 text-center text-sm text-muted-foreground">No hay temas con esa palabra.</p> : null}
      <div className="space-y-2">
        {principales.map((t) => {
          const hijos = hijosDe(t.id).filter(ver);
          const abierto = abiertos.has(t.id) || (!!busca && principales.length <= 3);
          return (
            <section key={t.id} id={`ayuda-${t.id}`} className="rounded-2xl border bg-card" data-testid={`ayuda-pagina-${t.id}`}>
              <button type="button" className="flex w-full items-start gap-3 px-4 py-3 text-left" onClick={() => alternar(t.id)}>
                {abierto ? <ChevronDown className="mt-0.5 h-4 w-4 shrink-0" /> : <ChevronRight className="mt-0.5 h-4 w-4 shrink-0" />}
                <span className="min-w-0 flex-1">
                  <span className="block font-semibold">{t.titulo}</span>
                  {abierto ? null : <span className="block truncate text-sm text-muted-foreground"><TextoAyuda texto={t.resumen} /></span>}
                </span>
                {t.manual ? <span className="shrink-0 text-[11px] text-muted-foreground"><BookOpen className="mr-1 inline h-3 w-3" />{t.manual}</span> : null}
              </button>
              {abierto ? (
                <div className="space-y-5 border-t px-4 py-4">
                  <ContenidoTema tema={t} alAbrir={ir} />
                  {hijos.map((h) => (
                    <div key={h.id} id={`ayuda-${h.id}`} className="space-y-2 border-t pt-4">
                      <h2 className="font-semibold">{h.titulo}</h2>
                      <ContenidoTema tema={h} alAbrir={ir} />
                    </div>
                  ))}
                </div>
              ) : null}
            </section>
          );
        })}
      </div>
    </div>
  );
}
